import {
    createOfferSummaryCard,
    IOfferSummaryCardAdditionalPrice,
    IOfferSummaryCardPromoPrice,
    OfferSummaryCard,
} from '@vfde-brix/ws10/offer-summary-card';
import {
    NO_PATTERN_BUSINESS_LOGIC,
    Price,
} from '@vfde-brix/ws10/core';
import { StateProps } from 'Container/App/interface';
import {
    composeLegalText,
    getMonthlyPrice,
    getPriceCondition,
    getPrimaryButtonContent,
    getSubline,
    shouldHideMonthlyPrice,
} from 'Container/App/helpers/offerSummaryCardHelper';
import { getShippingFee } from '@vfde-sails/page-options';
import { startAppListening } from 'Container/App/listener';
import { mapStateToProps } from 'Container/App/selectors';
import { regexReplace } from '@vfde-sails/utils';
import { ADDITIONAL_PAGE_OPTIONS } from '@vfde-sails/constants';
import mountStickyPriceBar from './StickyPriceBar';
import { PriceType } from '@vfde-sails/glados-v2';

const mountOfferSummaryCard = (containerId: string, stickyPriceBarContainerId: string): OfferSummaryCard | null => {
    const container = document.getElementById(containerId);

    /* istanbul ignore next */
    if (!container) {
        return null;
    }

    const offerSummaryCard = createOfferSummaryCard(container, NO_PATTERN_BUSINESS_LOGIC);
    const stickyPriceBar = mountStickyPriceBar(stickyPriceBarContainerId);

    stickyPriceBar && offerSummaryCard.connectStickyPriceBar(stickyPriceBar);

    startAppListening({
        predicate: (action, currentState, previousState) => {
            const currentStateProps = mapStateToProps(currentState);
            const previousStateProps = mapStateToProps(previousState);

            return currentStateProps.atomicDevice !== previousStateProps.atomicDevice
            || currentStateProps.subscription !== previousStateProps.subscription
            || currentStateProps.customerConfig.isHardwareOnly !== previousStateProps.customerConfig.isHardwareOnly
            || currentStateProps.customerConfig.paymentMethod !== previousStateProps.customerConfig.paymentMethod;
        },
        effect: (action, listenerApi) => {
            listenerApi.cancelActiveListeners();

            const stateProps = mapStateToProps(listenerApi.getState());

            updateOfferSummaryCard(offerSummaryCard, stateProps);
        },
    });

    // TODO: remove this after launch of new footnotes
    const observer = new MutationObserver((records: MutationRecord[]) => {
        for (const record of records) {
            if ((record.target as HTMLElement).querySelector('sup')) {
                (window as any).vf.footnotes.init();
                // It's enough to call this once
                break;
            }
        }
    });
    offerSummaryCard && observer.observe(offerSummaryCard.getContainerElement(), { attributes: false, childList: true, subtree: true });
    // TODO end: remove this after launch of new footnotes

    return offerSummaryCard;
};

/**
 * Update offerSummaryCard
 */
export const updateOfferSummaryCard = (offerSummaryCard: OfferSummaryCard, state: StateProps) => {
    const originalProperties = offerSummaryCard.getOriginalProperties();

    const { device } = state;

    /* istanbul ignore next */
    if (!device) {
        return;
    }

    const promoPrices = Object.values(
        getPromoPrices(state, originalProperties.containerPromoPrices || []),
    ).filter(Boolean) as IOfferSummaryCardPromoPrice[];

    const additionalPrices = getAdditionalPrices(state);

    offerSummaryCard
        .updateHeadline({
            stdContent: `${device!.modelName}, ${state.atomicDevice?.caseSize}`,
        })
        .updateSubline(getSubline(state, originalProperties))
        .updatePrices(
            promoPrices,
            Object.values(additionalPrices).filter(Boolean) as IOfferSummaryCardAdditionalPrice[],
        )
        .updateButtons([getPrimaryButtonContent(state, originalProperties.containerButtons!)])
        .updateLegalText(composeLegalText(state))
    ;

    offerSummaryCard.stickyPriceBar?.update({
        stickyPriceBarItems: [
            ...OfferSummaryCard.convertPromoPriceItemsToStickyPriceBarItems(promoPrices),
            ...OfferSummaryCard.convertAdditionalPriceItemsToStickyPriceBarItems(additionalPrices.stairway),
        ],
    });
};

const getPromoPrices = (state: StateProps, originalPrices: IOfferSummaryCardPromoPrice[]): {
    [PriceType.Onetime]: IOfferSummaryCardPromoPrice,
    [PriceType.Monthly]: IOfferSummaryCardPromoPrice | null,
} => {
    const { customerConfig, hardwareOneTimePrice } = state;
    const { isHardwareOnly, paymentMethod } = customerConfig;

    const hideMonthlyPrice = shouldHideMonthlyPrice(paymentMethod, isHardwareOnly);

    const prices: ReturnType<typeof getPromoPrices> = {
        [PriceType.Onetime]: {
            txtLabel: originalPrices[0].txtLabel,
            containerPromoPrice: {
                ...originalPrices[0].containerPromoPrice,
                price: Price.fromNumber(hardwareOneTimePrice!.gross),
                optPriceConditionBefore: getPriceCondition(customerConfig.isHardwareOnly, customerConfig.paymentMethod),
            },
        },
        [PriceType.Monthly]: null,
    };

    if (!hideMonthlyPrice) {
        prices[PriceType.Monthly] = {
            txtLabel: originalPrices[1].txtLabel,
            containerPromoPrice: {
                ...originalPrices[1].containerPromoPrice,
                ...getMonthlyPrice(state),
            },
        };
    }

    return prices;
};

const getAdditionalPrices = (state: StateProps): {
    stairway: IOfferSummaryCardAdditionalPrice | null,
    connectionFee: IOfferSummaryCardAdditionalPrice | null,
    shippingFee: IOfferSummaryCardAdditionalPrice | null,
} => {
    const prices: ReturnType<typeof getAdditionalPrices> = {
        stairway: null,
        connectionFee: null,
        shippingFee: null,
    };

    const {
        hasStairway,
        device,
        connectionFee,
    } = state;

    if (hasStairway) {
        const { tariffPriceAtRuntimeEnd, tariffPriceAtRuntimeStart } = state;

        prices.stairway = {
            txtLabel: regexReplace(window[ADDITIONAL_PAGE_OPTIONS].offerSummaryCard.legalTextStairway, [{
                regex: /([0-9]+)(?=\. Monat)/,
                replacement: `${tariffPriceAtRuntimeStart!.recurrenceEnd + 1}`,
            }]),
            price: Price.fromNumber(tariffPriceAtRuntimeEnd!.gross),
        };
    }

    if (connectionFee) {
        prices.connectionFee = {
            txtLabel: window[ADDITIONAL_PAGE_OPTIONS].offerSummaryCard.connectionFeeLabel,
            price: Price.fromNumber(connectionFee.gross),
        };
    }

    const shippingFee = device ? getShippingFee() : undefined;

    if (shippingFee) {
        prices.shippingFee = {
            txtLabel: shippingFee.label,
            price: Price.fromNumber(shippingFee.gross),
        };
    }

    return prices;
};

export default mountOfferSummaryCard;
